import { ViewportPortal } from "@xyflow/react";
import { useCanvasDrawStyle } from "../hooks/useCanvasDrawStyle";
import { useDrawPlacement } from "../hooks/useDrawPlacement";
import { drawingStrokePath } from "../utils/drawingStroke";
import { drawingTipInk } from "../utils/drawingTips";

interface CanvasDrawLayerProps {
  /** The draw tool is picked; the pane is listening for a stroke. */
  active: boolean;
}

/**
 * Ink in progress, committed to a `canvasDrawing` node on pointer up.
 *
 * The gesture lives here rather than in `CanvasPage` so a stroke's ~60Hz
 * samples re-render one SVG path instead of the whole canvas. Must sit inside
 * `<ReactFlow>`: the ghost rides the viewport transform through
 * `ViewportPortal`.
 */
export function CanvasDrawLayer({ active }: CanvasDrawLayerProps) {
  const { drawTip, drawColor, drawStrokeWidth, drawErasing } =
    useCanvasDrawStyle();
  const { strokePoints } = useDrawPlacement({
    active,
    tip: drawTip,
    color: drawColor,
    strokeWidth: drawStrokeWidth,
    erasing: drawErasing,
  });

  if (!strokePoints || strokePoints.length < 2) return null;

  const ink = drawingTipInk(drawTip, drawStrokeWidth);

  return (
    <ViewportPortal>
      <svg
        className="absolute pointer-events-none overflow-visible"
        style={{ left: 0, top: 0 }}
      >
        <path
          d={drawingStrokePath(strokePoints)}
          stroke={drawColor}
          strokeWidth={ink.strokeWidth}
          strokeLinecap={ink.linecap}
          strokeLinejoin="round"
          fill="none"
          opacity={ink.opacity}
        />
      </svg>
    </ViewportPortal>
  );
}
